/** format a Date as a local-timezone YYYY-MM-DD string (does not use toISOString, which shifts by timezone). */
export const toYmd = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** combine a date (from calendar) with a HH:mm time string, preserving local timezone offset.
 *  if the time string is malformed (e.g. user mid-edit, Backspace cleared the segment),
 *  the date's existing hours/minutes are preserved instead of poisoning the Date with NaN. */
export const combineDateAndTime = (date: Date, time: string): string => {
  const combined = new Date(date)
  const match = time.match(/^(\d{1,2}):(\d{2})$/)
  if (match) {
    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours <= 23 && minutes <= 59) {
      combined.setHours(hours, minutes, 0, 0)
    }
  }
  const tzOffset = -combined.getTimezoneOffset()
  const sign = tzOffset >= 0 ? '+' : '-'
  const pad = (n: number) => String(Math.abs(n)).padStart(2, '0')
  const offsetStr = `${sign}${pad(Math.floor(Math.abs(tzOffset) / 60))}:${pad(Math.abs(tzOffset) % 60)}`
  const yyyy = combined.getFullYear()
  const mm = pad(combined.getMonth() + 1)
  const dd = pad(combined.getDate())
  const hh = pad(combined.getHours())
  const min = pad(combined.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${min}:00${offsetStr}`
}

export const getTimeFromIso = (iso: string | Date): string => {
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** "now" as a create-form date value: local wall-clock to the minute, in the offset-ISO shape the
 *  date picker writes. one Date, so the day and the time can never come from different instants. */
export const nowEntryDate = (): string => {
  const now = new Date()
  return combineDateAndTime(now, getTimeFromIso(now))
}

/** the date a create-form should show after a "create another" save. a date the user explicitly
 *  picked carries into the next entry, so a chain of Shift+Enter saves stays on it; an untouched
 *  date falls back to now and is re-stamped at submit time, keeping same-day rows in entry order. */
export const nextEntryDate = (pickedIso: string | undefined, wasPicked: boolean): string =>
  wasPicked && pickedIso ? pickedIso : nowEntryDate()

/** HH:mm:ss — 24-hour clock with seconds, for dense/real-time feeds (e.g. activity/order logs). */
export const getTimeWithSecondsFromIso = (iso: string | Date): string => {
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/** DD MMM YYYY, HH:mm — date and 24-hour time where there is room (e.g. "05 Jan 2099, 15:05"). */
export const formatDateDisplay = (iso: string | Date): string => {
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${formatDateMedium(d)}, ${getTimeFromIso(d)}`
}

/** DD.MM.YYYY — compact numeric date for tight/dense columns (e.g. "05.01.2099"). */
export const formatDateShort = (iso: string | Date): string => {
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
}

/** DD MMM YYYY — readable date for roomier columns (e.g. "05 Jan 2099"). */
export const formatDateMedium = (iso: string | Date): string => {
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d)
}

/** DD.MM.YYYY HH:mm — used in tooltips on list rows where the visible cell shows date only. */
export const formatDateTimeShort = (iso: string | Date): string => {
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** the inverse of formatDateTimeShort: "DD.MM.YYYY HH:mm" back to an offset-ISO instant. replaces
 *  <input type="datetime-local">, which renders in the browser's locale (MM/DD and an AM/PM clock
 *  on an en-US Chrome) and offers no way to force a format.
 *
 *  returns null for anything half-typed, non-numeric, or impossible (31.02.2026), so the caller can
 *  leave the row's existing date alone instead of writing NaN — the guard combineDateAndTime uses. */
export const parseDateTimeShort = (value: string): string | null => {
  const match = value.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})[ T]+(\d{1,2}):(\d{2})$/)
  if (!match) return null

  const [, dayRaw, monthRaw, yearRaw, hourRaw, minuteRaw] = match
  const day = Number(dayRaw)
  const month = Number(monthRaw)
  const year = Number(yearRaw)
  const hours = Number(hourRaw)
  const minutes = Number(minuteRaw)
  if (hours > 23 || minutes > 59) return null

  const d = new Date(year, month - 1, day, hours, minutes, 0, 0)
  if (Number.isNaN(d.getTime())) return null
  // JS Date rolls out-of-range components over (31 Feb becomes 3 March), which would silently
  // accept an impossible date and move the row — require the parts to survive construction.
  if (d.getFullYear() !== year || d.getMonth() + 1 !== month || d.getDate() !== day) return null

  const pad = (n: number) => String(n).padStart(2, '0')
  return combineDateAndTime(d, `${pad(hours)}:${pad(minutes)}`)
}

/** true when the given instant falls on the current local calendar day. */
export const isToday = (iso: string | Date): boolean => {
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return false
  const now = new Date()
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  )
}

/** compact human-readable duration from milliseconds, showing at most the two largest non-zero
 *  units, e.g. "45s", "12m", "3h 20m", "2d 4h". non-positive or invalid input reads as "0s". */
export const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return '0s'
  const totalSeconds = Math.floor(ms / 1000)
  const days = Math.floor(totalSeconds / 86_400)
  const hours = Math.floor((totalSeconds % 86_400) / 3_600)
  const minutes = Math.floor((totalSeconds % 3_600) / 60)
  const seconds = totalSeconds % 60
  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (seconds > 0) parts.push(`${seconds}s`)
  return parts.slice(0, 2).join(' ') || '0s'
}
